import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { type JobRow, type JobStage, listJobs } from '@/lib/api'
import { RoleRow, RolesHead, STAGE_META } from '@/lib/rolesTable'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

// A real Delivery page: the roles currently at a given pipeline stage, in the standard roles table.
// Used by Submissions (submitted), Interviews (interview) and Placements (placement).
export function StageDeliveryPage({ stage, title, subtitle }: { stage: JobStage; title: string; subtitle: string }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<JobRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true)
    listJobs({ stage, pageSize: 100 })
      .then((res) => live && setRows(res.jobs))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [stage])

  return (
    <DashboardLayout title={title} navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="mb-3 flex items-center gap-2">
        <PageHeader title={title} subtitle={subtitle} />
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${STAGE_META[stage].chip}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${STAGE_META[stage].dot}`} /> {rows.length} {rows.length === 1 ? 'role' : 'roles'}
        </span>
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><RolesHead /></thead>
              <tbody className="divide-y divide-border">
                {rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    {loading ? 'Loading…' : `No roles at the ${STAGE_META[stage].label.toLowerCase()} stage yet. Advance a role from its job page to move it here.`}
                  </td></tr>
                ) : (
                  rows.map((j) => (
                    <RoleRow key={j.id} role={j} showCompany onClick={() => navigate({ to: '/jobs/$jobId', params: { jobId: j.id } })} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}
